
import React from "react";
import { Helmet } from "react-helmet-async";
import { isSSR } from "@/lib/isSSR";
import { createOrganizationSchema, createWebsiteSchema, combineSchemas } from "@/utils/schemaUtils";

interface StructuredData {
  '@context': string;
  '@type': string;
  [key: string]: any;
}

interface PageSEOProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  structuredData?: StructuredData | StructuredData[];
  noIndex?: boolean;
  ssrMode?: boolean;
  children?: React.ReactNode;
  keywords?: string[];
  ogType?: string;
  language?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  breadcrumbs?: { name: string; url: string }[];
  articleSection?: string;
  articleTags?: string[];
  readingTime?: string;
  wordCount?: number;
  geoTargeting?: {
    country?: string;
    region?: string;
    placename?: string;
    position?: string;
  };
  resourceHints?: {
    preconnect?: string[];
    dnsPrefetch?: string[];
    preload?: Array<{ href: string; as: string; type?: string }>;
  };
}

export const PageSEO = ({
  title,
  description,
  canonicalPath,
  ogImage,
  structuredData,
  noIndex = false,
  ssrMode = false,
  children,
  keywords,
  ogType = "website",
  language = "it-IT",
  author,
  publishedTime,
  modifiedTime,
  breadcrumbs,
  articleSection,
  articleTags,
  readingTime,
  wordCount,
  geoTargeting,
  resourceHints
}: PageSEOProps) => {
  // Determine if we're in server-side rendering
  const isServerRendering = isSSR();
  
  // Build full canonical URL
  const canonicalUrl = `https://www.gildy.it${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`;
  
  // Default image URL if not specified
  const imageUrl = ogImage || "https://www.gildy.it/logo.png";
  
  // Prepare structured data
  let jsonLdData: string = '';
  
  if (structuredData) {
    if (Array.isArray(structuredData)) {
      // If it's an array, combine all schemas
      jsonLdData = combineSchemas(...structuredData);
    } else {
      // If it's a single schema, combine with organization and website schemas
      jsonLdData = combineSchemas(
        structuredData,
        createOrganizationSchema(),
        createWebsiteSchema()
      );
    }
  } else {
    // Default schemas if none provided
    jsonLdData = combineSchemas(
      createOrganizationSchema(),
      createWebsiteSchema()
    );
  }
  
  // Return the Helmet content directly since HelmetProvider is already provided in entry-server.tsx
  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Resource Hints for Performance */}
      {resourceHints?.preconnect?.map((url, index) => (
        <link key={`preconnect-${index}`} rel="preconnect" href={url} />
      ))}
      {resourceHints?.dnsPrefetch?.map((url, index) => (
        <link key={`dns-prefetch-${index}`} rel="dns-prefetch" href={url} />
      ))}
      {resourceHints?.preload?.map((resource, index) => (
        <link 
          key={`preload-${index}`} 
          rel="preload" 
          href={resource.href} 
          as={resource.as}
          type={resource.type}
        />
      ))}
      
      {/* Geo-targeting Meta Tags */}
      {geoTargeting?.country && (
        <meta name="geo.region" content={geoTargeting.country} />
      )}
      {geoTargeting?.placename && (
        <meta name="geo.placename" content={geoTargeting.placename} />
      )}
      {geoTargeting?.position && (
        <meta name="geo.position" content={geoTargeting.position} />
      )}
      {geoTargeting?.region && (
        <meta name="ICBM" content={geoTargeting.region} />
      )}
      
      {/* Keywords */}
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      {/* Author */}
      {author && <meta name="author" content={author} />}
      
      {/* Reading time metadata */}
      {readingTime && <meta name="reading-time" content={readingTime} />}
      {wordCount && <meta name="word-count" content={String(wordCount)} />}
      
      {/* Mobile App Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Gildy" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Gildy" />
      <meta property="og:locale" content={language} />
      
      {/* Enhanced Open Graph tags for blog articles */}
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {ogType === 'article' && articleSection && (
        <meta property="article:section" content={articleSection} />
      )}
      {ogType === 'article' && articleTags && articleTags.length > 0 && articleTags.map((tag, index) => (
        <meta key={`tag-${index}`} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@gildy_it" />
      
      {/* Twitter author */}
      {author && <meta name="twitter:creator" content={`@${author.replace(/\s/g, '').toLowerCase()}`} />}
      
      {/* Reading time for social platforms */}
      {readingTime && <meta name="twitter:label1" content="Tempo di lettura" />}
      {readingTime && <meta name="twitter:data1" content={readingTime} />}
      
      {/* Robots meta tag */}
      {noIndex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Performance Hints */}
      <meta name="referrer" content="origin-when-cross-origin" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="color-scheme" content="light dark" />
      
      {/* Structured data (JSON-LD) */}
      <script type="application/ld+json">
        {jsonLdData}
      </script>
      
      {/* Additional SEO meta tags */}
      <meta name="fragment" content="!" />
      <meta name="prerender" content="active" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {children}
    </Helmet>
  );
};
